import { useState, useEffect } from "react";
import { PlusCircle, Search } from "lucide-react";
import dayjs from "dayjs";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Skeleton } from "src/components/ui/skeleton";
import { toast } from "sonner";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import axios from "axios";
import { useLanguage } from "../../../../context/LanguageContext";
import { PricesData } from "../../../../lib/languages";
import AddPriceDialog from "./_components/AddPriceDialog";
import EditPriceDialog from "./_components/EditPriceDialog";
import DeletePriceDialog from "./_components/DeletePriceDialog";
import FeaturesDialog from "./_components/FeaturesDialog";
import PriceCard from "./_components/PriceCard";
import CardSkeleton from "../../../../components/common/CardSkeleton";
import { sub } from "date-fns";
import MainButton from "./../../../../components/MainButton";

export default function TeacherPrices() {
  const { language } = useLanguage();
  const strings = PricesData[language] || PricesData["en"];

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFeatureDialogOpen, setIsFeatureDialogOpen] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [editingFeature, setEditingFeature] = useState(null);
  const [errors, setErrors] = useState({});

  const [btnLoading, setBtnLoading] = useState({
    create: false,
    update: false,
    delete: false,
    feature: false,
    toggleStatus: false,
  });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    currency_id: 1,
    discount: 0,
    duration: "01:00",
    type: "individual",
    group_num: 0,
    lesson_num: 0,
    category_id: null,
    sub_category_id: null,
  });

  const fetchPrices = async () => {
    try {
      setErrors({});
      const { data } = await axios.get(`${BaseUrlApi}/prices`);
      setPrices(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      setErrors({});
      const { data } = await axios.get(`${BaseUrlApi}/currencies`);
      setCurrencies(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setErrors({});
      const { data } = await axios.get(`${BaseUrlApi}/teacher/categories`);
      setCategories(data.data);
      setSubCategories(
        data.data.flatMap((category) => category.sub_categories)
      );
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchPrices();
    fetchCurrencies();
    fetchCategories();
  }, []);

  const filteredPrices = prices.filter((price) =>
    price.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === "price" || name === "discount" || name === "currency_id"
          ? Number.parseFloat(value)
          : value,
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleTimeChange = (time) => {
    if (time) {
      const duration = time.format("HH:mm");
      setFormData({ ...formData, duration });
    }
  };

  const handleCreatePrice = async () => {
    setBtnLoading((prev) => ({ ...prev, create: true }));
    try {
      await axios.post(`${BaseUrlApi}/prices`, formData);
      await fetchPrices();
      setIsAddDialogOpen(false);
      resetForm();
      toast(strings.success, { description: strings.priceCreated });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdatePrice = async () => {
    if (!selectedPrice) return;
    setBtnLoading((prev) => ({ ...prev, update: true }));
    try {
      await axios.put(`${BaseUrlApi}/prices/${selectedPrice.bid}`, formData);
      await fetchPrices();
      setIsEditDialogOpen(false);
      setSelectedPrice(null);
      resetForm();
      toast(strings.success, { description: strings.priceUpdated });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleDeletePrice = async () => {
    if (!selectedPrice) return;
    setBtnLoading((prev) => ({ ...prev, delete: true }));
    try {
      await axios.delete(`${BaseUrlApi}/prices/${selectedPrice.bid}`);
      await fetchPrices();
      setIsDeleteDialogOpen(false);
      setSelectedPrice(null);
      toast(strings.success, { description: strings.priceDeleted });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleAddFeature = async () => {
    if (!selectedPrice || !newFeature) return;
    setBtnLoading((prev) => ({ ...prev, feature: true }));
    try {
      const { data } = await axios.post(
        `${BaseUrlApi}/price/${selectedPrice.id}/price-features`,
        { description: newFeature }
      );
      const updatedPrices = prices.map((price) => {
        if (price.id === selectedPrice.id) {
          return {
            ...price,
            price_feature: [...price.price_feature, data.data],
          };
        }
        return price;
      });
      setPrices(updatedPrices);
      setNewFeature("");
      const updatedSelectedPrice = updatedPrices.find(
        (p) => p.id === selectedPrice.id
      );
      if (updatedSelectedPrice) setSelectedPrice(updatedSelectedPrice);
      toast(strings.success, { description: strings.featureAdded });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, feature: false }));
    }
  };

  const handleUpdateFeature = async () => {
    if (!selectedPrice || !editingFeature) return;
    setBtnLoading((prev) => ({ ...prev, feature: true }));
    try {
      await axios.put(
        `${BaseUrlApi}/price/${selectedPrice.id}/price-features/${editingFeature.bid}`,
        { description: newFeature }
      );
      const updatedPrices = prices.map((price) => {
        if (price.id === selectedPrice.id) {
          return {
            ...price,
            price_feature: price.price_feature.map((feature) =>
              feature.id === editingFeature.id
                ? { ...feature, description: newFeature }
                : feature
            ),
          };
        }
        return price;
      });
      setPrices(updatedPrices);
      setNewFeature("");
      setEditingFeature(null);
      const updatedSelectedPrice = updatedPrices.find(
        (p) => p.id === selectedPrice.id
      );
      if (updatedSelectedPrice) setSelectedPrice(updatedSelectedPrice);
      toast(strings.success, { description: strings.featureUpdated });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, feature: false }));
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (!selectedPrice) return;
    try {
      const feature = selectedPrice.price_feature.find(
        (f) => f.id === featureId
      );
      await axios.delete(
        `${BaseUrlApi}/price/${selectedPrice.id}/price-features/${feature.bid}`
      );
      const updatedPrices = prices.map((price) => {
        if (price.id === selectedPrice.id) {
          return {
            ...price,
            price_feature: price.price_feature.filter(
              (f) => f.id !== featureId
            ),
          };
        }
        return price;
      });
      setPrices(updatedPrices);
      const updatedSelectedPrice = updatedPrices.find(
        (p) => p.id === selectedPrice.id
      );
      if (updatedSelectedPrice) setSelectedPrice(updatedSelectedPrice);
      toast(strings.success, { description: strings.featureDeleted });
    } catch (error) {
      console.error("Error deleting feature:", error);
    }
  };

  const handleToggleStatus = async (price) => {
    setBtnLoading((prev) => ({ ...prev, toggleStatus: true }));
    try {
      await axios.put(`${BaseUrlApi}/prices/${price.bid}`, {
        name: price.name,
        currency_id: price.currency.id,
        price: price.price,
        type: price.type,
        status: price.status === 1 ? 0 : 1,
      });
      const updatedPrices = prices.map((p) => {
        if (p.id === price.id) {
          return { ...p, status: p.status === 1 ? 0 : 1 };
        }
        return p;
      });
      setPrices(updatedPrices);
      toast(strings.success, {
        description:
          price.status === 1
            ? strings.priceDeactivated
            : strings.priceActivated,
      });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast(strings.error, { description: result });
      setErrors(result);
    } finally {
      setBtnLoading((prev) => ({ ...prev, toggleStatus: false }));
    }
  };

  const resetForm = () => {
    setSelectedPrice(null);
    setFormData({
      name: "",
      price: 0,
      currency_id: 1,
      discount: 0,
      duration: "01:00",
      type: "individual",
      group_num: 0,
      lesson_num: 0,
    });
  };

  const prepareFormForEdit = (price) => {
    setFormData({
      name: price.name,
      price: price.price,
      currency_id: price.currency.id,
      discount: price.discount,
      duration: dayjs(price.duration, "HH:MM").format("HH:MM"),
      type: price.type,
      group_num: price.group_num,
      lesson_num: price.lesson_num,
      category_id: price.category?.id,
      sub_category_id: price.subCategory?.id,
    });
    setSelectedPrice(price);
    setIsEditDialogOpen(true);
  };

  const formatCurrency = (price, currency) =>
    `${currency.symbol || currency.code} ${price}`;

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {strings.priceManagement}
          </h1>
          <p className="text-muted-foreground">{strings.priceManagementDesc}</p>
        </div>
        <MainButton
          Icon={PlusCircle}
          title={strings.addNewPrice}
          onClick={() => setIsAddDialogOpen(true)}
        />
      </div>

      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={strings.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {loading ? (
        <CardSkeleton />
      ) : (
        <PriceCard
          filteredPrices={filteredPrices}
          searchTerm={searchTerm}
          strings={strings}
          setIsAddDialogOpen={setIsAddDialogOpen}
          setIsFeatureDialogOpen={setIsFeatureDialogOpen}
          setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          setSelectedPrice={setSelectedPrice}
          prepareFormForEdit={prepareFormForEdit}
          btnLoading={btnLoading}
          handleToggleStatus={handleToggleStatus}
          formatCurrency={formatCurrency}
        />
      )}
      <AddPriceDialog
        setFormData={setFormData}
        categories={categories}
        subCategories={subCategories}
        isAddDialogOpen={isAddDialogOpen}
        setIsAddDialogOpen={setIsAddDialogOpen}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        resetForm={resetForm}
        handleCreatePrice={handleCreatePrice}
        handleTimeChange={handleTimeChange}
        btnLoading={btnLoading}
        currencies={currencies}
        errors={errors}
      />

      <EditPriceDialog
        setFormData={setFormData}
        categories={categories}
        subCategories={subCategories}
        isEditDialogOpen={isEditDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        resetForm={resetForm}
        handleUpdatePrice={handleUpdatePrice}
        handleTimeChange={handleTimeChange}
        btnLoading={btnLoading}
        currencies={currencies}
        errors={errors}
      />

      <DeletePriceDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        selectedPrice={selectedPrice}
        handleDeletePrice={handleDeletePrice}
        btnLoading={btnLoading}
        resetForm={resetForm}
        formatCurrency={formatCurrency}
      />

      <FeaturesDialog
        isFeatureDialogOpen={isFeatureDialogOpen}
        setIsFeatureDialogOpen={setIsFeatureDialogOpen}
        selectedPrice={selectedPrice}
        btnLoading={btnLoading}
        newFeature={newFeature}
        setNewFeature={setNewFeature}
        handleUpdateFeature={handleUpdateFeature}
        handleAddFeature={handleAddFeature}
        setEditingFeature={setEditingFeature}
        handleDeleteFeature={handleDeleteFeature}
        resetForm={resetForm}
        editingFeature={editingFeature}
        errors={errors}
      />
    </div>
  );
}
